import { useEffect, useMemo } from 'react';
import type { ChangeEventHandler, RefObject } from 'react';
import { Camera, Loader2, Mail, Sparkles, Upload, UserPen, SwatchBook } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES, translations, type SupportedLanguage } from '@/i18n/translations';
import { generateGoogleFontsUrl, getHeadingTopOffset, getHomeHeadingOpticalOffset, GOOGLE_FONTS } from '@/lib/googleFonts';
import { optimizeCloudinaryUrl } from '@/lib/utils';
import type { ThemeTokens } from '@/types/theme';

type SemanticThemePreviewCardProps = {
  tokens: ThemeTokens;
  title: string;
  subtitle: string;
  language: string;
  coverImageUrl: string;
  newCoverPreviewUrl: string | null;
  showGuestbook: boolean;
  showInvitation: boolean;
  savingCover: boolean;
  fileInputRef: RefObject<HTMLInputElement | null>;
  onCoverFileChange: ChangeEventHandler<HTMLInputElement>;
  openCoverFileDialog: () => void;
  onApplyNewCover: () => Promise<void>;
  resetNewCover: () => void;
  showSupportCard?: boolean;
};

function createCssVars(tokens: ThemeTokens) {
  const vars: Record<string, string> = {};

  Object.entries(tokens.colors).forEach(([key, value]) => {
    if (value) {
      vars[`--${key}`] = value;
    }
  });

  if (tokens.fonts?.general) {
    vars['--font-sans'] = tokens.fonts.general;
  }

  if (tokens.fonts?.headings) {
    vars['--font-heading'] = tokens.fonts.headings;
  }

  return vars;
}

function extractGoogleFontName(fontFamily?: string) {
  const primaryFamily = fontFamily?.split(',')[0]?.trim().replace(/['"]/g, '');
  if (!primaryFamily) {
    return null;
  }

  return GOOGLE_FONTS.find((font) => font.name === primaryFamily)?.name || null;
}

function getPreviewLanguage(language: string): SupportedLanguage {
  return SUPPORTED_LANGUAGES.includes(language as SupportedLanguage)
    ? language as SupportedLanguage
    : DEFAULT_LANGUAGE;
}

function tPreview(key: string, language: SupportedLanguage) {
  return translations[key]?.[language] || translations[key]?.[DEFAULT_LANGUAGE] || key;
}

const SemanticThemePreviewCard = ({
  tokens,
  title,
  subtitle,
  language,
  coverImageUrl,
  newCoverPreviewUrl,
  showGuestbook,
  showInvitation,
  savingCover,
  fileInputRef,
  onCoverFileChange,
  openCoverFileDialog,
  onApplyNewCover,
  resetNewCover,
  showSupportCard = true,
}: SemanticThemePreviewCardProps) => {
  const previewStyle = createCssVars(tokens);
  const previewLanguage = getPreviewLanguage(language);
  const optimizedCoverUrl = useMemo(() => {
    if (newCoverPreviewUrl) {
      return newCoverPreviewUrl;
    }

    return optimizeCloudinaryUrl({
      originalUrl: coverImageUrl,
      width: 900,
      height: 1800,
    });
  }, [coverImageUrl, newCoverPreviewUrl]);
  const headingTopOffset = getHeadingTopOffset(tokens.fonts?.headings);
  const headingShiftY = getHomeHeadingOpticalOffset(tokens.fonts?.headings);

  useEffect(() => {
    const fontNames = [tokens.fonts?.general, tokens.fonts?.headings]
      .map((fontFamily) => extractGoogleFontName(fontFamily))
      .filter((fontName): fontName is string => Boolean(fontName));

    const uniqueFontNames = [...new Set(fontNames)];
    const googleFontsUrl = uniqueFontNames.length > 0 ? generateGoogleFontsUrl(uniqueFontNames) : '';
    const existingLink = document.getElementById('google-fonts-theme-preview');

    if (!googleFontsUrl) {
      existingLink?.remove();
      return;
    }

    if (existingLink instanceof HTMLLinkElement) {
      if (existingLink.getAttribute('href') !== googleFontsUrl) {
        existingLink.href = googleFontsUrl;
      }
      return;
    }

    const link = document.createElement('link');
    link.id = 'google-fonts-theme-preview';
    link.rel = 'stylesheet';
    link.href = googleFontsUrl;
    document.head.appendChild(link);
  }, [tokens.fonts?.general, tokens.fonts?.headings]);

  const supportCard = (
    <div
      className="rounded-[24px] border p-5 mx-2 shadow-[0_12px_26px_rgba(15,23,42,0.08)]"
      style={{
        ...previewStyle,
        backgroundColor: 'hsl(var(--background))',
        borderColor: 'hsl(var(--border))',
        color: 'hsl(var(--foreground))',
        fontFamily: 'var(--font-sans, ui-sans-serif, system-ui, sans-serif)',
      }}
    >


      <h4
        className="mt-4 flex items-start text-xl font-semibold"
        style={{
          color: 'hsl(var(--foreground))',
        }}
      >
        <SwatchBook className="h-6 w-6 mr-2" />
        <span
          className="block leading-tight"
          style={{
            fontFamily: 'var(--font-heading, var(--font-sans, ui-sans-serif, system-ui, sans-serif))',
            paddingTop: headingTopOffset,
            transform: `translateY(${headingShiftY})`,
          }}
        >
          Otros elementos
        </span>
      </h4>

      <p className="mt-2 text-sm leading-6" style={{ color: 'hsl(var(--muted-foreground))' }}>
        Así se ven las tarjetas, bordes, etiquetas y estados secundarios que son derivados a partir de tus colores principales.
      </p>

      <div className="mt-4 flex flex-wrap gap-2">
        <div
          className="inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold"
          style={{
            backgroundColor: 'hsl(var(--accent))',
            color: 'hsl(var(--accent-foreground))',
          }}
        >
          Más recientes
        </div>
        <div
          className="inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium"
          style={{
            borderColor: 'hsl(var(--border))',
            color: 'hsl(var(--muted-foreground))',
          }}
        >
          Más votadas
        </div>
      </div>

      <div
        className="mt-5 rounded-[22px] border p-4"
        style={{
          backgroundColor: 'hsl(var(--card))',
          borderColor: 'hsl(var(--border))',
        }}
      >
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-semibold" style={{ color: 'hsl(var(--card-foreground))' }}>
              Invitados compartiendo fotos
            </div>
            <div className="mt-1 text-xs" style={{ color: 'hsl(var(--muted-foreground))' }}>
              Botones, bordes y textos usan el set derivado.
            </div>
          </div>
        </div>

        <div className="mt-4 flex flex-col gap-3">
          <Button
            type="button"
            className="w-full h-12 bg-primary hover:bg-secondary text-primary-foreground font-medium rounded-lg transition-colors"
          >
            <Upload className="w-4 h-4 mr-2" />
            Botón principal
          </Button>
          <Button
            type="button"
            variant="outline"
            className="w-full h-12 font-medium rounded-lg transition-colors"
          >
            Botón secundario
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-5">
      <div className="flex justify-center">
        <div className="inline-flex items-center gap-2 rounded-full bg-white/90 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-violet-600 shadow-sm">
          <Sparkles className="h-3.5 w-3.5" />
          Vista previa
        </div>
      </div>

      <div className="space-y-5">
        <div
          role="button"
          tabIndex={0}
          onClick={openCoverFileDialog}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              openCoverFileDialog();
            }
          }}
          aria-label="Cambiar imagen de portada"
          className="group relative mx-auto w-full max-w-[320px] cursor-pointer"
        >
          <div className="rounded-[42px] bg-slate-950 p-[9px] shadow-[0_28px_60px_rgba(15,23,42,0.22)] ring-1 ring-slate-900/15">
            <div className="relative aspect-[390/844] overflow-hidden rounded-[34px] bg-slate-200">
              <img
                src={optimizedCoverUrl}
                alt={title}
                className="absolute inset-0 h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/25" />

              {showInvitation ? (
                <div
                  className="absolute left-5 top-6 z-10 inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium shadow-sm backdrop-blur-sm"
                  style={{
                    ...previewStyle,
                    backgroundColor: `hsl(${tokens.colors.background} / 0.78)`,
                    borderColor: `hsl(${tokens.colors.border} / 0.45)`,
                    color: 'hsl(var(--foreground))',
                    fontFamily: 'var(--font-sans, ui-sans-serif, system-ui, sans-serif)',
                  }}
                >
                  <Mail className="h-4 w-4" />
                  {tPreview('invitation', previewLanguage)}
                </div>
              ) : null}

              <div
                className="absolute inset-x-5 bottom-6 z-10 rounded-[30px] p-6 text-center shadow-[0_18px_40px_rgba(15,23,42,0.18)] backdrop-blur-md"
                style={{
                  ...previewStyle,
                  backgroundColor: `hsl(${tokens.colors.background} / 0.74)`,
                  color: 'hsl(var(--foreground))',
                  fontFamily: 'var(--font-sans, ui-sans-serif, system-ui, sans-serif)',
                }}
              >
                <h3
                  className="text-[2.15rem] font-light leading-tight text-foreground"
                  style={{
                    fontFamily: 'var(--font-heading, var(--font-sans, ui-sans-serif, system-ui, sans-serif))',
                    paddingTop: headingTopOffset,
                    transform: `translateY(${headingShiftY})`,
                  }}
                >
                  {title}
                </h3>

                {subtitle.trim() ? (
                  <p className="mt-5 text-lg font-light leading-tight text-foreground">{subtitle}</p>
                ) : null}

                <div className="mt-6 space-y-4 pointer-events-none">
                  <div className="mx-auto flex h-14 w-full max-w-[230px] items-center justify-center rounded-xl bg-primary text-md font-medium text-primary-foreground shadow-sm transition-colors">
                    <Camera className="mr-2 h-5 w-5" strokeWidth={2} />
                    {tPreview('viewAlbum', previewLanguage)}
                  </div>

                  {showGuestbook ? (
                    <div className="mx-auto flex h-14 w-full max-w-[230px] items-center justify-center rounded-xl bg-primary text-md font-medium text-primary-foreground shadow-sm transition-colors">
                      <UserPen className="mr-2 h-5 w-5" strokeWidth={2} />
                      {tPreview('viewGuestbook', previewLanguage)}
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="pointer-events-none absolute inset-0 bg-black/25 opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-focus-visible:opacity-100" />
              <div className="pointer-events-none absolute inset-x-0 top-36 z-10 flex justify-center opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-focus-visible:opacity-100">
                <div className="inline-flex items-center gap-2 rounded-full bg-white/90 px-4 py-2 text-sm font-semibold text-slate-900 shadow-lg">
                  <Camera className="h-4 w-4" />
                  Cambiar portada
                </div>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png"
                className="hidden"
                onChange={onCoverFileChange}
              />
            </div>
          </div>
        </div>

        {newCoverPreviewUrl ? (
          <div className="flex items-center justify-center gap-3">
            <Button type="button" variant="outline" onClick={resetNewCover} disabled={savingCover}>
              Cancelar
            </Button>
            <Button type="button" onClick={() => { void onApplyNewCover(); }} disabled={savingCover} className="panel-cta">
              {savingCover ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Aplicando…
                </span>
              ) : (
                'Aplicar portada'
              )}
            </Button>
          </div>
        ) : null}
      </div>

      {showSupportCard ? supportCard : null}
    </div>
  );
};

export default SemanticThemePreviewCard;
